import { Component, OnInit, OnDestroy } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { Subject } from 'rxjs';

import { FormField } from '../lib/form-field.model';
import {
  WorkflowFormFieldFormComponent,
  WorkflowFormFieldFormOptions,
  WorkflowFormFieldFormResult
} from './form-field-form.component';
import { WorkflowFormFieldItemPermissionEvent } from './form-field-item.component';
import { Form } from '../lib/form.model';
import { FormPermission } from '../lib/form-permission.model';
import { State } from '../lib/state.model';
import { FormEditorService } from './form-editor.service';
import { WorkflowEditorStore } from '../lib/workflow-editor.store';

@Component({
    selector: 'eas-workflow-form-editor',
    templateUrl: './form-editor.component.html',
    styleUrls: ['./form-editor.component.css'],
    providers: [
        {
            provide: FormEditorService,
            useFactory: (workflowEditor: WorkflowEditorStore) => {
                return workflowEditor.createFormEditor();
            },
            deps: [WorkflowEditorStore]
        }
    ],
    standalone: false
})
export class WorkflowFormEditorComponent implements OnInit, OnDestroy {
  public get availableStates(): State[] {
    return this.formEditor.availableStates;
  }

  public get form(): Form|undefined {
    return this.formEditor.form;
  }

  public get availableFields(): FormField[] {
    return this.formEditor.allFormFields;
  }

  private destroyed$: Subject<void> = new Subject<void>();
  private dialog: MatDialog;
  private dialogRef?:
    MatDialogRef<WorkflowFormFieldFormComponent, WorkflowFormFieldFormResult>;
  private formEditor: FormEditorService;

  constructor(dialog: MatDialog, formEditor: FormEditorService) {
    this.dialog = dialog;
    this.formEditor = formEditor;
  }

  ngOnInit(): void { }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  //
  // Actions
  //

  public onAddFormField(): void {
    this.presentFormFieldFormDialog(undefined, (result) => {
      if (!result || !(result instanceof FormField)) { return; }
      this.formEditor.addFormField(result);
    });
  }

  public onEditFormField(id: string): void {
    const field = this.formEditor.form.findFieldByID(id);
    if (!field) { return; }

    this.presentFormFieldFormDialog(field, (result) => {
      if (!result) { return; }
      if (result instanceof FormField) {
        this.formEditor.patchFormField(id, result);
      }
      if (typeof result === 'string') {
        this.formEditor.deleteFormField(result);
      }
    });
  }

  public onSetPermissionForAllFields(editable: boolean) {
    this.formEditor.setFormFieldsPermission(this.formEditor.form.fields, true, editable ?? false);
  }

  public onSetFormFieldPermission(event: WorkflowFormFieldItemPermissionEvent): void {
    const field = this.formEditor.form.findFieldByID(event.id);
    if (!field) { return; }
    this.formEditor.setFormFieldPermission(field, true, event.editable ?? false);
  }

  public onRemoveFormFieldPermission(event: WorkflowFormFieldItemPermissionEvent): void {
    const field = this.formEditor.form.findFieldByID(event.id);
    if (!field) { return; }
    this.formEditor.removeFormFieldPermission(event.id);
  }

  public onMoveFieldUp(id: string): void {
    this.formEditor.moveFieldUp(id);
  }

  public onMoveFieldDown(id: string): void {
    this.formEditor.moveFieldDown(id);
  }

  public onDropField(event: { id: string, to: number }): void {
    this.formEditor.moveFieldTo(event.id, event.to);
  }

  //
  // Private stuff
  //

  private presentFormFieldFormDialog(object: FormField|undefined,
                                     afterClosed?: (result: WorkflowFormFieldFormResult) => void): void {
    if (this.dialogRef || !this.form) { return; }

    let perms: FormPermission[] = [];
    if (object) {
      perms = this.form.findPermissionsBy(object.id);
    }

    const options: MatDialogConfig<WorkflowFormFieldFormOptions> = {
      minWidth: '60vw',
      maxWidth: '70vw',
      minHeight: '80vh',
      id: 'workflow-editor-form-dialog',
      data: {
        item: object,
        fieldTypes: this.formEditor.availableFieldTypes,
        permissions: perms,
        fields: this.formEditor.allFormFields,
        availableRoles: this.formEditor.availableRoles,
      }
    };

    this.dialogRef =
      this.dialog.open<WorkflowFormFieldFormComponent,
                       WorkflowFormFieldFormOptions,
                       WorkflowFormFieldFormResult>(WorkflowFormFieldFormComponent, options);
    this.dialogRef.afterClosed().subscribe(result => {
      this.dialogRef = undefined;
      if (afterClosed && result) { afterClosed(result); }
    });
  }
}
